import React, {useState, useEffect, useRef} from "react";
import ChatHistory from "./irisComponent/ChatHistory";
import ChatInput from "./irisComponent/ChatInput";
import Spinner from "./irisComponent/Spinner";
import {Message} from "../types/types";

const WEBSOCKET_URL =
  "wss://aespasol-testdomain.duckdns.org/chatbot/ws/irisAWS/";

const IrisChatBotBlk = () => {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  // Connect WebSocket
  useEffect(() => {
    const socket = new WebSocket(WEBSOCKET_URL);
    socketRef.current = socket;
    socket.onopen = () => {
      console.log("✅ WebSocket connected");
      setIsSocketConnected(true);
    };

    socket.onclose = () => {
      console.log("❌ WebSocket disconnected");
      setIsSocketConnected(false);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.thinking) {
          setIsThinking(true);
          return;
        }

        const sender = data?.botResponse?.role;
        const rawMessage = data?.botResponse?.content[0]?.text || "";

        const imageObject: Record<string, string> =
          data?.images && typeof data.images === "object" ? data.images : {};

        const formattedMessage = rawMessage
          .replace(
            /<url=([^>]+)>(.*?)<\/url>/g,
            (_: any, url: any, text: any) => {
              return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
          )
          .replace(/\[([^\]]+)\]/g, (match: any, filename: any) => {
            const url = imageObject[filename];
            if (url) {
              return `<img src="${url}" alt="${filename}" style="max-width:100%; margin:10px 0; display:block;" />`;
            }
            return match;
          });

        const botMessage: Message = {
          sender,
          message: formattedMessage,
          imageArray: Object.values(imageObject),
        };

        setMessages((prev) => [...prev, botMessage]);
        setIsLoading(false);
        setIsThinking(false);
      } catch (error) {
        console.error("Error parsing message:", error);
        setIsLoading(false);
        setIsThinking(false);
      }
    };

    return () => socket.close();
  }, []);

  useEffect(() => {
    const updateVH = () => {
      document.documentElement.style.setProperty(
        "--vh",
        `${window.innerHeight * 0.01}px`
      );
    };
    updateVH();
    window.addEventListener("resize", updateVH);
    window.addEventListener("orientationchange", updateVH);

    return () => {
      window.removeEventListener("resize", updateVH);
      window.removeEventListener("orientationchange", updateVH);
    };
  }, []);

  const onSubmitHandler = (message?: string) => {
    const val =
      typeof message === "string" ? message.trim() : inputValue.trim();

    if (
      !val ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    ) {
      console.warn("⚠️ Cannot send message: WebSocket not connected.");
      return;
    }

    const userMessage: Message = {sender: "user", message: val};
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    socketRef.current.send(JSON.stringify({message: val}));
    setInputValue("");
  };

  return (
    <div className="root-container-blk" style={{position: "relative"}}>
      <div className="iris-container">
        {!isSocketConnected ? (
          <Spinner variant="large-spinner" alignSpin="center-spinner" />
        ) : (
          <React.Fragment>
            <div
              className={`iris-center-content ${
                messages.length > 0 ? "messageNotEmpty" : "messageEmpty"
              }`}>
              <h1
                className={`iris-title ${messages.length ? "fade-out" : ""} ${
                  isExpanded ? "sidebar-expanded" : ""
                }`}>
                iRis - your agentic CX AI Concierge
              </h1>

              <ChatHistory
                messages={messages}
                bottomRef={bottomRef}
                isLoading={isLoading}
                isThinking={isThinking}
                setInputValue={setInputValue}
                onSubmit={onSubmitHandler}
              />
            </div>

            <ChatInput
              messages={messages}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onSubmit={onSubmitHandler}
              isExpanded={isExpanded}
            />
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default IrisChatBotBlk;
